/**
 * Status Toggle Hook
 * 
 * Custom hook that provides status toggle functionality for entities.
 * Handles the confirmation dialog, loading states, and status updates.
 * 
 * @module useStatusToggle
 */

import { useState } from 'react';
import { message } from 'antd';
import { TOGGLE_ACTIONS, TOGGLE_MESSAGES } from '../constants/filter-constants';

/**
 * Custom hook for status toggle functionality
 * 
 * @param {Function} toggleService - Service function to call for status toggle
 * @param {Function} onSuccess - Callback to call after successful toggle
 * @param {string} module - Module name for appropriate messaging
 * @returns {Object} Status toggle utilities and state
 * 
 * @example
 * const {
 *   isDialogOpen,
 *   isLoading,
 *   entityToToggle,
 *   toggleAction,
 *   handleToggleClick,
 *   handleConfirm,
 *   handleCancel
 * } = useStatusToggle(FacilityService.toggleStatus, onRefresh, 'facility');
 */
export const useStatusToggle = (toggleService, onSuccess, module) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [entityToToggle, setEntityToToggle] = useState(null);
  const [toggleAction, setToggleAction] = useState('');

  /**
   * Initiates the status toggle process
   * 
   * @param {Object} entity - Entity to toggle (facility, user, provider)
   * @param {string} action - Action to perform ('activate' or 'deactivate')
   */
  const handleToggleClick = (entity, action) => {
    setEntityToToggle(entity);
    setToggleAction(action);
    setIsDialogOpen(true);
  };

  /**
   * Confirms and executes the status toggle
   */
  const handleConfirm = async () => {
    if (!entityToToggle || !toggleAction) return;

    setIsLoading(true);
    const newStatus = toggleAction === TOGGLE_ACTIONS.ACTIVATE;
    
    try {
      const result = await toggleService(entityToToggle._id, newStatus);
      
      if (result.success) {
        message.success(result.message);
        
        // Call success callback if provided
        if (onSuccess) {
          await onSuccess();
        }
      } else {
        message.error(result.message);
      }
    } catch (error) {
      console.error(`Error toggling ${module} status:`, error);
      message.error(error.message || `Failed to ${toggleAction} ${module}. Please try again.`);
    } finally {
      setIsLoading(false);
      setIsDialogOpen(false);
      setEntityToToggle(null);
      setToggleAction('');
    }
  };

  /**
   * Cancels the status toggle operation
   */
  const handleCancel = () => {
    setIsDialogOpen(false);
    setEntityToToggle(null);
    setToggleAction('');
  };

  /**
   * Gets the confirmation dialog message
   * 
   * @returns {string} Confirmation message
   */
  const getConfirmationMessage = () => {
    if (!entityToToggle || !toggleAction) {
      return `Are you sure you want to change the ${module} status?`;
    }

    const entityName = entityToToggle.facilityName || 
                      entityToToggle.fullName || 
                      entityToToggle.name || 
                      'this item';
    
    const moduleMessages = TOGGLE_MESSAGES[module] || TOGGLE_MESSAGES.facility;
    const actionMessage = moduleMessages[toggleAction] || '';
    
    return `Are you sure you want to ${toggleAction} "${entityName}"? ${actionMessage}`;
  };

  /**
   * Gets the appropriate icon and color for the toggle button
   * 
   * @param {boolean} isActive - Current active status
   * @returns {Object} Icon component and color
   */
  const getToggleButtonProps = (isActive) => {
    return {
      action: isActive ? TOGGLE_ACTIONS.DEACTIVATE : TOGGLE_ACTIONS.ACTIVATE,
      color: isActive ? '#f57c00' : '#2e7d32', // Orange for deactivate, green for activate
      tooltipText: isActive ? `Deactivate ${module}` : `Activate ${module}`
    };
  };

  /**
   * Checks if user has permission to toggle status
   * 
   * @param {string} userRole - Current user's role
   * @returns {boolean} Whether user can toggle status
   */
  const canToggleStatus = (userRole) => {
    // For now, only admins can toggle status
    // This can be expanded based on module-specific permissions
    return userRole === 'admin';
  };

  return {
    isDialogOpen,
    isLoading,
    entityToToggle,
    toggleAction,
    handleToggleClick,
    handleConfirm,
    handleCancel,
    getConfirmationMessage,
    getToggleButtonProps,
    canToggleStatus
  };
};
